import { useCallback, useEffect, useRef, useState } from 'react';
import { getInfluenceAnalytics, getInfluenceCategories } from '../../../services/influencesService';
import { fetchNetwork, IntelligenceUnavailableError } from '../intelligenceApi';
import { InfluenceModel, modelFromAnalytics, modelFromNetwork } from './influenceModel';

export type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * Loads the influence model from the intelligence network (scores + ties), falling back to the
 * older analytics endpoint when the server has no intelligence module yet.
 * `reload({ quiet: true })` keeps the current model on screen so changes animate in place.
 */
export function useInfluenceData() {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [model, setModel] = useState<InfluenceModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const requestRef = useRef(0);

  const reload = useCallback(async (options: { quiet?: boolean } = {}) => {
    const request = ++requestRef.current;
    if (options.quiet) setRefreshing(true);
    else setStatus('loading');
    setError(null);
    try {
      let next: InfluenceModel;
      try {
        const [graph, categories] = await Promise.all([fetchNetwork(), getInfluenceCategories().catch(() => [])]);
        next = modelFromNetwork(graph, Array.isArray(categories) ? categories : []);
      } catch (err) {
        if (!(err instanceof IntelligenceUnavailableError)) throw err;
        next = modelFromAnalytics(await getInfluenceAnalytics());
      }
      if (request !== requestRef.current) return;
      setModel(next);
      setStatus('ready');
    } catch (err: any) {
      if (request !== requestRef.current) return;
      setError(err?.response?.data?.message || err?.message || 'Failed to load influence data');
      setStatus((s) => (options.quiet && s === 'ready' ? 'ready' : 'error'));
    } finally {
      if (request === requestRef.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { status, model, error, refreshing, reload };
}
